import { ThemeEvents } from '@theme/events';

class ProductStickyAtc extends HTMLElement {
  #intersecting = new Set();
  #selectedOptionValueId = null;

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.variantPicker =
      this.section?.querySelector('variant-picker[data-template-product-match="true"]') ??
      this.section?.querySelector('variant-picker');

    this.sizePanel = this.querySelector('.product-sticky-atc__panel');
    this.sizeTrigger = this.querySelector('[data-sticky-atc-size-trigger]');
    this.sizeLabel = this.querySelector('[data-sticky-atc-size-label]');
    this.stickyAtc = this.querySelector('[data-sticky-atc-button]');
    this.stickyPrice = this.querySelector('[data-sticky-atc-price]');

    this.sizeTrigger?.addEventListener('click', this.#onTriggerClick);
    this.sizePanel?.addEventListener('click', this.#onPanelClick);
    this.stickyAtc?.addEventListener('click', this.#onAtcClick);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);

    this.section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.section?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);

    const sentinel =
      this.section?.querySelector('.product-form-buttons.spacing-style.product-form-buttons--stacked') ??
      this.section?.querySelector('.product-form-buttons.spacing-style');
    const footer = document.querySelector('[id$="__footer_custom_main"]');

    const onIntersect = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#intersecting.add(entry.target);
        } else {
          this.#intersecting.delete(entry.target);
        }
      }
      const visible = this.#intersecting.size === 0;
      if (!visible) this.#closePanel();
      this.classList.toggle('sticky-atc--visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) this.#primeWillChangeCleanup(this);
    };

    if (sentinel || footer) {
      this.io = new IntersectionObserver(onIntersect, {
        threshold: 0,
        rootMargin: '-103px 0px -103px 0px',
      });
      if (sentinel) this.io.observe(sentinel);
      if (footer) this.io.observe(footer);
    }

    this.#syncInitial();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.section?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  // ─── Panel open / close ───────────────────────────────────────────────────

  #openPanel() {
    if (!this.sizePanel) return;
    this.sizePanel.setAttribute('open', '');
    this.sizePanel.removeAttribute('aria-hidden');
    this.sizeTrigger?.setAttribute('aria-expanded', 'true');
    this.#primeWillChangeCleanup(this.sizePanel);
  }

  #closePanel() {
    if (!this.sizePanel || !this.sizePanel.hasAttribute('open')) return;
    this.sizePanel.removeAttribute('open');
    this.sizePanel.setAttribute('aria-hidden', 'true');
    this.sizeTrigger?.setAttribute('aria-expanded', 'false');
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  #onTriggerClick = () => {
    if (this.sizePanel?.hasAttribute('open')) {
      this.#closePanel();
    } else {
      this.#openPanel();
    }
  };

  #onPanelClick = (e) => {
    const row = e.target.closest('.product-sticky-atc__panel-row');
    if (!row || row.disabled) return;

    const { optionValueId, sizeLabel } = row.dataset;
    if (!optionValueId) return;

    const radio = this.variantPicker?.querySelector(
      `input[type="radio"][data-option-value-id="${CSS.escape(optionValueId)}"]`
    );
    radio?.click();

    this.#setSelected(optionValueId, sizeLabel);
    this.#closePanel();
  };

  #onDocumentClick = (e) => {
    if (!this.contains(e.target)) this.#closePanel();
  };

  #onKeydown = (e) => {
    if (e.key === 'Escape' && this.sizePanel?.hasAttribute('open')) {
      this.#closePanel();
      this.sizeTrigger?.focus();
    }
  };

  #onAtcClick = (e) => {
    e.preventDefault();
    if (this.sizeTrigger && !this.#selectedOptionValueId) {
      this.#openPanel();
      return;
    }
    const realBtn = this.section?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    realBtn?.click();
  };

  #onVariantUpdate = () => {
    this.#syncSizeLabel();
    this.#syncDisabledState();
    requestAnimationFrame(() => {
      const priceEl = this.section?.querySelector('product-price .price');
      if (priceEl && this.stickyPrice) {
        this.stickyPrice.textContent = priceEl.textContent.trim();
      }
    });
  };

  #onVariantSelected = () => {
    this.#syncDisabledState();
  };

  // ─── State helpers ────────────────────────────────────────────────────────

  /** Update the trigger label and aria-selected on all panel rows. */
  #setSelected(optionValueId, label) {
    this.#selectedOptionValueId = optionValueId;
    if (this.sizeLabel) this.sizeLabel.textContent = `Size ${label}`;
    this.sizePanel?.querySelectorAll('.product-sticky-atc__panel-row').forEach((row) => {
      row.setAttribute('aria-selected', row.dataset.optionValueId === optionValueId ? 'true' : 'false');
    });
  }

  #syncInitial() {
    this.#syncSizeLabel();
    this.#syncDisabledState();
    const priceEl = this.section?.querySelector('product-price .price');
    if (priceEl && this.stickyPrice) {
      this.stickyPrice.textContent = priceEl.textContent.trim();
    }
  }

  #syncSizeLabel() {
    if (!this.sizeLabel || !this.sizePanel) return;
    const checkedRadio = this.variantPicker?.querySelector(
      'fieldset.variant-option--buttons input[type="radio"]:checked'
    );
    if (!checkedRadio) return;
    const optionValueId = checkedRadio.dataset.optionValueId ?? '';
    const matchingRow = this.sizePanel.querySelector(
      `[data-option-value-id="${CSS.escape(optionValueId)}"]`
    );
    if (matchingRow) this.#setSelected(optionValueId, checkedRadio.value);
  }

  #syncDisabledState() {
    if (!this.stickyAtc) return;
    const realBtn = this.section?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    this.stickyAtc.disabled = realBtn?.disabled ?? false;
  }

  /** Release GPU promotion after stagger animations finish. Mirrors cart-drawer.js:144-158. */
  #primeWillChangeCleanup(root) {
    const clear = (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', clear);
      }
    };
    root?.querySelectorAll('.sticky-atc-stagger-item').forEach((el) => {
      el.style.removeProperty('will-change'); // let the CSS rule reapply on next reveal
      el.addEventListener('animationend', clear);
    });
  }
}

if (!customElements.get('product-sticky-atc')) {
  customElements.define('product-sticky-atc', ProductStickyAtc);
}
